import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Package, Lock, CheckCircle2, AlertTriangle, Loader2, Layers } from "lucide-react";
import { api, equivalencesApi, formatCurrency } from "@/lib/api";
import { toast } from "sonner";

/**
 * Section "Matières (par défaut)" sur la fiche commande détaillée.
 * Affiche les "produits type" configurés sur le type de commande + l'état
 * de verrouillage sur cette commande. Permet de saisir surface/épaisseur
 * et de verrouiller en cliquant.
 */
export default function OrderMaterialsTemplateSection({ order }) {
  const [data, setData] = useState({ templates: [], order_type_id: null });
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({}); // template_id → { surface, thickness, allow_equivalence }
  const [groupAvailability, setGroupAvailability] = useState({}); // group_id → availability data
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    if (!order?.id) return;
    try {
      setLoading(true);
      const r = await equivalencesApi.getOrderTemplates(order.id);
      setData(r.data || { templates: [] });
      // Pre-fill draft from default values
      const next = {};
      (r.data?.templates || []).forEach((t) => {
        // If already locked, use locked values
        const locked = (t.locked_materials || [])[0];
        next[t.id] = {
          surface: locked?.surface_m2 ?? t.default_quantity ?? "",
          thickness: locked?.thickness_mm ?? t.default_thickness_mm ?? "",
          allow_equivalence: locked?.allow_equivalence ?? t.allow_equivalence ?? true,
        };
      });
      setDraft(next);
      // Fetch availability for each group template
      const groupTpls = (r.data?.templates || []).filter((t) => t.equivalence_group_id);
      const avail = {};
      await Promise.all(groupTpls.map(async (t) => {
        try {
          const av = await equivalencesApi.getGroupStockAvailability(t.equivalence_group_id);
          avail[t.equivalence_group_id] = av.data;
        } catch { /* noop */ }
      }));
      setGroupAvailability(avail);
    } catch (e) {
      // Order might have no type
    } finally {
      setLoading(false);
    }
  }, [order?.id]);

  useEffect(() => { load(); }, [load]);

  const lockTemplate = async (t) => {
    const d = draft[t.id] || {};
    if (!d.surface || parseFloat(d.surface) <= 0) {
      toast.error("Saisis une quantité > 0");
      return;
    }
    // Cost estimation: simple — use product price if available
    let totalCost = 0;
    if (t.product_id && t.product_reference) { /* enrichment is partial; we keep 0 for now */ }
    const payload = {
      product_id: t.product_id || null,
      equivalence_group_id: t.equivalence_group_id || null,
      surface_m2: parseFloat(d.surface),
      thickness_mm: d.thickness ? parseFloat(d.thickness) : null,
      total_cost: totalCost,
      allow_equivalence: t.equivalence_group_id ? true : !!d.allow_equivalence,
    };
    try {
      setSavingId(t.id);
      await api.lockOrderMaterial(order.id, payload);
      toast.success("Matière verrouillée");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSavingId(null);
    }
  };

  const unlockTemplate = async (mat) => {
    if (!window.confirm("Retirer le verrouillage ?")) return;
    try {
      await api.deleteOrderMaterial(mat.id);
      toast.success("Retiré");
      await load();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-6 flex items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Chargement matières…
        </CardContent>
      </Card>
    );
  }

  if (!data.order_type_id || data.templates.length === 0) {
    return null; // Pas de type ou aucun template défini → on n'affiche rien
  }

  return (
    <Card data-testid="order-materials-template-section">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Package className="h-4 w-4 text-blue-700" />
          Matières (produits type)
          <Badge variant="secondary" className="text-[10px]">{data.templates.length}</Badge>
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Complète les quantités et verrouille les matières. Elles remontent automatiquement
          dans Stock & Prévisionnel pour anticiper tes commandes fournisseurs.
        </p>
      </CardHeader>
      <CardContent>
        <ul className="divide-y border rounded-md">
          {data.templates.map((t) => {
            const locked = (t.locked_materials || [])[0];
            const isLocked = !!locked;
            const d = draft[t.id] || {};
            const groupAvail = t.equivalence_group_id ? groupAvailability[t.equivalence_group_id] : null;
            return (
              <li key={t.id} className="px-3 py-3 space-y-2 text-sm" data-testid={`mat-tpl-row-${t.id}`}>
                <div className="flex items-center gap-2">
                  {t.equivalence_group_id ? (
                    <Layers className="h-4 w-4 text-purple-600 shrink-0" />
                  ) : (
                    <Package className="h-4 w-4 text-blue-600 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">
                      {t.product_name || t.group_name}
                      {t.equivalence_group_id && (
                        <Badge variant="secondary" className="ml-2 text-[10px]">Groupe d&apos;équivalence</Badge>
                      )}
                      {isLocked && (
                        <Badge className="ml-2 text-[10px] bg-emerald-100 text-emerald-800">
                          <CheckCircle2 className="h-3 w-3 mr-0.5" /> Verrouillé
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {t.supplier_name || (t.equivalence_group_id ? "Multi-fournisseurs" : "—")}
                      {" · "}
                      {t.reference_unit || ""}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                  <div className="md:col-span-3">
                    <Label className="text-xs">Surface / quantité</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={d.surface ?? ""}
                      onChange={(e) => setDraft((s) => ({ ...s, [t.id]: { ...d, surface: e.target.value } }))}
                      placeholder={t.default_quantity ? String(t.default_quantity) : ""}
                      disabled={isLocked}
                      data-testid={`mat-tpl-surface-${t.id}`}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Épaisseur (mm)</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={d.thickness ?? ""}
                      onChange={(e) => setDraft((s) => ({ ...s, [t.id]: { ...d, thickness: e.target.value } }))}
                      disabled={isLocked}
                      data-testid={`mat-tpl-thickness-${t.id}`}
                    />
                  </div>
                  {t.product_id && (
                    <div className="md:col-span-4 flex items-center gap-1.5 text-xs">
                      <Switch
                        checked={!!d.allow_equivalence}
                        onCheckedChange={(v) => setDraft((s) => ({ ...s, [t.id]: { ...d, allow_equivalence: v } }))}
                        disabled={isLocked}
                        data-testid={`mat-tpl-allow-equiv-${t.id}`}
                      />
                      Autoriser équivalences
                    </div>
                  )}
                  <div className="md:col-span-3 ml-auto flex gap-2 justify-end">
                    {isLocked ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => unlockTemplate(locked)}
                        data-testid={`mat-tpl-unlock-${t.id}`}
                      >
                        Retirer
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => lockTemplate(t)}
                        disabled={savingId === t.id}
                        data-testid={`mat-tpl-lock-${t.id}`}
                      >
                        {savingId === t.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Lock className="h-3 w-3 mr-1" />}
                        Verrouiller
                      </Button>
                    )}
                  </div>
                </div>
                {/* Group availability hint */}
                {t.equivalence_group_id && groupAvail && (
                  <div className="text-xs bg-purple-50 border border-purple-200 rounded p-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-purple-900">
                        Stock disponible cumulé : {groupAvail.total_available_for_new} {groupAvail.reference_unit}
                      </span>
                      {d.surface > 0 && groupAvail.total_available_for_new < parseFloat(d.surface) && (
                        <Badge className="bg-red-100 text-red-800 text-[10px]">
                          <AlertTriangle className="h-3 w-3 mr-0.5" />
                          Manque {(parseFloat(d.surface) - groupAvail.total_available_for_new).toFixed(2)} {groupAvail.reference_unit}
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {(groupAvail.products || []).filter((p) => p.available_for_new > 0).map((p) => (
                        <Badge
                          key={p.product_id}
                          variant="outline"
                          className="text-[10px]"
                          title={`Stock: ${p.current_stock} - Conso prévue: ${p.upcoming_consumption}`}
                        >
                          {p.available_for_new} {p.reference_unit} chez {p.supplier_name || "—"}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
